import { useCallback } from "react";
import { Tabs } from "@design-system/Tabs/Tabs";
import { Chat } from "@components/mainInterface/chat/Chat";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { Task, TaskType } from "@common/types/task";
import { trackEvent } from "@lib/analytics";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useTaskPaneState, useTasksContext } from "../tasks.context";
import { CHAT_TAB_DROP_ZONE_ID } from "../chatDrag/chatDropZones";
import { useChatComposerDropZone } from "../chatDrag/useChatComposerDrop";
import { useSpringLoadedDropZone } from "../chatDrag/useSpringLoadedDropZone";
import { TaskDetail } from "./TaskDetail";
import { TaskDependencyRows } from "./TaskDependencyRows";
import { TaskDetailPanelType } from "./constants";
import {
    getTaskDetailPanelTabs,
    resolveTaskDetailPanelTab,
} from "./taskDetailPanelTabs";
import styles from "./TaskDetail.module.scss";

const TAB_LABELS: Record<TaskDetailPanelType, string> = {
    chat: "Chat",
    detail: "Details",
};

const SECTION_NAMES: Record<TaskDetailPanelType, string> = {
    chat: "chat",
    detail: "details",
};

interface TaskDetailPanelProps {
    selectedTask: Task;
}

/** The open task's right pane: Chat and Details tabs. */
export const TaskDetailPanel = ({ selectedTask }: TaskDetailPanelProps) => {
    const { openOverlay } = useOverlays();
    const { taskDetailPanelContent } = useTaskPaneState();
    const { setTaskDetailPanelContent } = useTasksContext();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const setChatPaneDropZoneRef = useChatComposerDropZone("main");
    const setChatTabDropZoneRef = useSpringLoadedDropZone({
        id: CHAT_TAB_DROP_ZONE_ID,
        onActivate: () => setTaskDetailPanelContent("chat"),
    });

    const handleCreateTask = useCallback(() => {
        // Opens a create form the server can only refuse on save (PRO-11345).
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("createTask", {
            type: TaskType.QUESTION,
            initialValues: {
                assigneeId: selectedTask.assigneeId ?? undefined,
                priority: selectedTask.priority ?? undefined,
                dueDate: selectedTask.dueDate ?? undefined,
                entityIds: selectedTask.entityIds ?? undefined,
                groupId: selectedTask.groupId ?? undefined,
                closeId: selectedTask.closeId ?? undefined,
                closeStageId: selectedTask.closeStageId ?? undefined,
            },
        });
    }, [openOverlay, refuseIfViewOnly, selectedTask]);

    const tabs = getTaskDetailPanelTabs(selectedTask);
    const activeTab = resolveTaskDetailPanelTab({
        tabs,
        requestedTab: taskDetailPanelContent,
    });

    const handleTabChange = (tab: TaskDetailPanelType) => {
        void trackEvent(AnalyticsEvent.TASK_DETAIL_SECTION_CLICKED, {
            section: SECTION_NAMES[tab],
            action: "select",
            taskId: selectedTask.id,
            taskType: selectedTask.type,
        });
        setTaskDetailPanelContent(tab);
    };

    return (
        <Tabs.Root
            value={activeTab}
            onValueChange={(value) => {
                const tab = tabs.find((candidate) => candidate === value);
                if (tab) {
                    handleTabChange(tab);
                }
            }}
            className={styles.paneTabs}
        >
            <Tabs.List className={styles.paneTabList} hasIndicator={false}>
                {tabs.map((tab) => (
                    <Tabs.Tab
                        key={tab}
                        value={tab}
                        className={styles.paneTab}
                        ref={tab === "chat" ? setChatTabDropZoneRef : undefined}
                    >
                        {TAB_LABELS[tab]}
                    </Tabs.Tab>
                ))}
            </Tabs.List>
            {selectedTask.thread && tabs.includes("chat") ? (
                <Tabs.Panel
                    value="chat"
                    className={styles.paneChatPanel}
                    keepMounted
                    ref={setChatPaneDropZoneRef}
                >
                    <Chat
                        selectedThread={selectedTask.thread}
                        setSelectedThread={() => {}}
                        hideHeader
                        autoFocusInput={false}
                        onCreateTask={handleCreateTask}
                        isVisible={activeTab === "chat"}
                    />
                </Tabs.Panel>
            ) : null}
            <Tabs.Panel value="detail" className={styles.panePanel}>
                <TaskDetail
                    task={selectedTask}
                    dependencyRows={<TaskDependencyRows task={selectedTask} />}
                />
            </Tabs.Panel>
        </Tabs.Root>
    );
};
